import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, FlaskConical, Leaf, Sprout, Star } from 'lucide-react';
import { type Category, type Product } from './shopData';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
import { useBestSellers } from '../../../utils/useBestSellers';
import { useReviewStats } from '../../../utils/useReviewStats';
import { getStorefrontProducts } from '../../../utils/catalog';
import { useCatalogProducts } from '../../../utils/useCatalogProducts';
export function Shop() {
  const [activeFilter, setActiveFilter] = useState<'All' | Category | 'Bestsellers'>('All');
  const [selectedCategories, setSelectedCategories] = useState<Set<Category>>(new Set());
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get('search')?.trim().toLowerCase() ?? '';
  const {
    addItem
  } = useCart();
  const catalogProducts = useCatalogProducts();
  const storefrontProducts = getStorefrontProducts(catalogProducts);
  const bestSellers = useBestSellers();
  const reviewStats = useReviewStats();
  const categoryCounts = storefrontProducts.reduce((acc, product) => {
    product.categories.forEach(category => {
      acc[category] = (acc[category] || 0) + 1;
    });
    return acc;
  }, {} as Record<Category, number>);
  let filtered = storefrontProducts;
  if (activeFilter === 'Bestsellers') {
    filtered = storefrontProducts.filter(product => bestSellers.has(product.id));
  } else if (activeFilter !== 'All') {
    filtered = storefrontProducts.filter(product => product.categories.includes(activeFilter));
  }
  if (selectedCategories.size > 0) {
    filtered = filtered.filter(product => product.categories.some(category => selectedCategories.has(category)));
  }
  if (searchTerm) {
    filtered = filtered.filter(product => {
      const searchableContent = [product.name, product.tagline, product.shortDescription, ...product.categories].join(' ').toLowerCase();
      return searchableContent.includes(searchTerm);
    });
  }
  const toggleCategory = (category: Category) => {
    const next = new Set(selectedCategories);
    if (next.has(category)) next.delete(category);else next.add(category);
    setSelectedCategories(next);
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-3017" data-mp="3lnl3lthrbe71">
      <HeroCarousel data-id="element-3018" data-mp="3poemg8gce2t2" />

      <div className="w-full h-[40px] bg-tealslate text-white flex items-center justify-center text-sm font-bold tracking-wide" data-id="element-3019" data-mp="3po4x8n34fzof">
        Free USA Shipping Over $99
      </div>

      <section className="py-12 md:py-16 px-6" data-id="element-3020" data-mp="2xxgyfhox76dx">
        <div className="max-w-7xl mx-auto" data-id="element-3021" data-mp="2xwpe530rwv2y">
          <div className="text-sm text-[#5A5A5A] mb-6" data-id="element-3022" data-mp="31ysqruz6i5pt">
            <Link to="/" className="hover:text-tealslate transition-colors underline decoration-[#E5E5E5] underline-offset-4" data-id="element-3023" data-mp="31x9c28t682q5">
              Home
            </Link>{' '}
            <span className="mx-2" data-id="element-3024" data-mp="2pgzrxrcved3n">/</span> Shop All Supplements
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-[#1F1F1F] mb-4 tracking-tight" data-id="element-3025" data-mp="2lda639nky9mb">
            Shop All Supplements
          </h1>
          <p className="text-base text-[#5A5A5A] mb-10" data-id="element-3026" data-mp="2ghuz91nzpdze">
            Explore our full range of clean, science-backed supplements.
          </p>

          <div className="flex flex-wrap gap-3 mb-12" data-id="element-3027" data-mp="26ont3elfdty9">
            {['All', 'Bestsellers', 'Protein', 'Strength', 'Endurance', 'Health', 'Stress', 'Sleep'].map(filter => <button key={filter} type="button" onClick={() => setActiveFilter(filter as 'All' | Category | 'Bestsellers')} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${activeFilter === filter ? 'bg-[#1F1F1F] text-white border border-[#1F1F1F]' : 'bg-white border border-[#E5E5E5] text-[#5A5A5A] hover:border-[#1F1F1F] hover:text-[#1F1F1F]'}`} data-id="element-3028" data-mp="1somhb43mh240">
                {filter === 'All' ? 'All Products' : filter}
              </button>)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10" data-id="element-3029" data-mp="394g9o82r030q">
            <aside className="lg:col-span-1" data-id="element-3030" data-mp="391v7tj2g9n2z">
              <h2 className="text-2xl font-bold text-[#1F1F1F] mb-6" data-id="element-3031" data-mp="2v5gkmzbbpm41">
                Filter products
              </h2>
              <details open className="group border-t border-[#E5E5E5] pt-5" data-id="element-3032" data-mp="3041iwt70lvpu">
                <summary className="cursor-pointer list-none flex items-center justify-between text-base font-bold text-[#1F1F1F] mb-4" data-id="element-3033" data-mp="te3ey81j7a6x">
                  Category
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" data-id="element-3034" data-mp="2itxofyx013gx" />
                </summary>
                <div className="space-y-3 pb-5" data-id="element-3035" data-mp="2dvvwsplibvtu">
                  {(['Protein', 'Strength', 'Endurance', 'Health', 'Stress', 'Sleep'] as Category[]).map(category => <label key={category} className="flex items-center justify-between cursor-pointer text-sm text-[#5A5A5A] hover:text-[#1F1F1F] transition-colors" data-id="element-3036" data-mp="2wllzrzzfbnwu">
                      <span className="flex items-center gap-3" data-id="element-3037" data-mp="36ji68ehf1uom">
                        <input type="checkbox" checked={selectedCategories.has(category)} onChange={() => toggleCategory(category)} className="w-4 h-4 rounded border-[#E5E5E5] text-[#1F1F1F] focus:ring-[#1F1F1F]" data-id="element-3038" data-mp="1h4u02uzf58z1" />
                        {category}
                      </span>
                      <span className="text-xs text-[#9A9A90]" data-id="element-3039" data-mp="2auuden0a4klu">
                        ({categoryCounts[category] || 0})
                      </span>
                    </label>)}
                </div>
              </details>
            </aside>

            <div className="lg:col-span-3" data-id="element-3040" data-mp="2ksiavkp9s67p">
              {filtered.length === 0 ? <div className="border border-dashed border-[#D8D8D0] bg-[#FAFAFA] px-6 py-14 text-center" data-id="element-3041" data-mp="2ossqzq6s0d0a">
                  <h2 className="text-lg font-bold text-[#1F1F1F]" data-id="element-3042" data-mp="2tlu69zawaeke">
                    No products found
                  </h2>
                  <p className="text-sm text-[#5A5A5A] mt-2" data-id="element-3043" data-mp="33j8e5air5v13">
                    Try a different category, or clear your filters to view the
                    full collection.
                  </p>
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3044" data-mp="ww11g3ijt1rh">
                  {filtered.map(product => <ShopProductCard key={product.id} product={product} addItem={addItem} isBestseller={bestSellers.has(product.id)} reviewStats={reviewStats[product.id]} data-id="element-3045" data-mp="rxzgte5h4udw" />)}
                </div>}
            </div>
          </div>
        </div>
      </section>

      <ReviewsCarousel data-id="element-3046" data-mp="15vo04hjw7tgu" />
    </div>;
}
function ShopProductCard({
  product,
  addItem,
  isBestseller,
  reviewStats
}: {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
  isBestseller: boolean;
  reviewStats?: {
    avg: number;
    count: number;
  };
}) {
  const {
    cents
  } = usePrice(product.lookupKey, product.price);
  if (product.id === "inulin-fiber") {
    console.log("INULIN", {
      lookupKey: product.lookupKey,
      price: product.price,
      cents,
      stock: product.stock
    });
  }
  console.log("PRODUCT", product);
  console.log("LOOKUP", {
    id: product.id,
    lookupKey: product.lookupKey,
    cents
  });
  const galleryImages = product.backImage && product.backImage !== product.image ? [product.image, product.backImage] : [product.image];
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const activeImage = galleryImages[activeImageIndex];
  const hasMultipleImages = galleryImages.length > 1;
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  const changeImage = (direction: 1 | -1) => {
    setActiveImageIndex(current => (current + direction + galleryImages.length) % galleryImages.length);
  };
  return <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white transition-[background-color,box-shadow] duration-200 ease-out hover:bg-[#E5E9F1] hover:shadow-lg" data-id="element-3047" data-mp="58zi1uty257j">      <div className="relative aspect-square shrink-0 bg-transparent p-6" data-id="element-3048" data-mp="3otx67gwgoaa2">
        {isBestseller && <span className="absolute top-3 left-3 z-20 px-2.5 py-1 bg-[#1F73B7] text-white text-[10px] font-bold tracking-wide rounded-sm" data-id="element-3049" data-mp="6mhev6tpf1qs">
            Best Seller
          </span>}
        <Link to={`/shop/${product.id}`} className="absolute inset-6 block" aria-label={`View ${product.name}`} data-id="element-3050" data-mp="3a5k6q9y609at">
          <AnimatePresence mode="wait" initial={false} data-id="element-3051" data-mp="3jvna942wx5ne">
            <motion.img key={activeImage} src={activeImage} alt={product.name} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.18
          }} className="absolute inset-0 h-full w-full object-contain" data-id="element-3052" data-mp="3jx6qiuokjgp6" />
          </AnimatePresence>
        </Link>
        {hasMultipleImages && <div className="absolute inset-x-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-between" data-id="element-3053" data-mp="1yz5b9ce9qi0f">
            <button type="button" onClick={() => changeImage(-1)} className="site-nav-icon-button inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D8D0] bg-white/95 shadow-sm transition-colors" aria-label={`Show previous image of ${product.name}`} data-id="element-3054" data-mp="1tz9eny7whsuw">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} data-id="element-3055" data-mp="1g731ml01o1zo" />
            </button>
            <button type="button" onClick={() => changeImage(1)} className="site-nav-icon-button inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D8D0] bg-white/95 shadow-sm transition-colors" aria-label={`Show next image of ${product.name}`} data-id="element-3056" data-mp="1b9ues73yb9s3">
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} data-id="element-3057" data-mp="38x5ck47kiiju" />
            </button>
          </div>}
      </div>

      <div className="flex flex-1 flex-col p-5" data-id="element-3058" data-mp="3dv6uumx66du9">
        <div className="flex items-center gap-0.5 mb-3" data-id="element-3059" data-mp="3dy2qg4ljoqfj">
          {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-3.5 h-3.5 ${star <= Math.round(reviewStats?.avg || 0) ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-3060" data-mp="3hvqsdrlnnjgx" />)}
          <span className="text-xs text-[#9A9A90] ml-1.5" data-id="element-3061" data-mp="2u8ipthfm2oil">
            {reviewStats?.count || 0} Reviews
          </span>
        </div>

        <Link to={`/shop/${product.id}`} className="hover:underline decoration-[#1F1F1F] underline-offset-2" data-id="element-3062" data-mp="33xsfyrqw9x42">
          <h3 className="text-lg font-bold text-[#1F1F1F] mb-2 leading-tight" data-id="element-3063" data-mp="26hd9z29o7w8a">
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-[#5A5A5A] mb-5 line-clamp-2 flex-1" data-id="element-3064" data-mp="2bfeqpgjmjjgl">
          {product.shortDescription}
        </p>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#E5E5E5]" data-id="element-3065" data-mp="39tbur1guqcjn">
          <p className="text-lg font-bold text-[#1F1F1F]" data-id="element-3066" data-mp="3nkpbk7ndkwaz">
            {cents !== null ? '$' + (cents / 100).toFixed(2) : '—'}
          </p>
          <button type="button" onClick={handleAdd} disabled={cents === null || !product.lookupKey} className="px-5 py-2.5 bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors disabled:opacity-40 rounded" data-id="element-3067" data-mp="3jk6m2k8yg2by">
            Add To Cart
          </button>
        </div>
      </div>
    </div>;
}
const HERO_SLIDE = {
  bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
  eyebrow: 'Science-backed. Dietitian approved.',
  headlineStart: 'Supplements that support your',
  headlineEnd: 'best life.',
  subhead: 'High-quality, evidence-based supplements to help you feel better, every day.',
  primaryCta: {
    label: 'Shop All Supplements',
    href: '#products'
  }
};
function HeroCarousel() {
  return <section className="relative min-h-[426px] md:h-[426px] px-6 pt-[91px] overflow-hidden" data-id="element-3068" data-mp="dnzxfd09qn5c">
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: `url('${HERO_SLIDE.bg}')`
    }} data-id="element-3069" data-mp="imtaj4bn8c4z" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40 pointer-events-none" data-id="element-3070" data-mp="16dgoo7c5jxzz" />

      <div className="relative h-full max-w-7xl mx-auto flex items-center" data-id="element-3071" data-mp="1baz147pdovcq">
        <div className="w-full pt-2 md:pt-0" data-id="element-3072" data-mp="wlkqkwa2g7vr">
          <p className="text-[10px] md:text-[11px] font-bold text-cream/90 uppercase tracking-[0.25em] mb-3" data-id="element-3073" data-mp="wkrpaatvmal0">
            {HERO_SLIDE.eyebrow}
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white leading-[1.05] mb-1" data-id="element-3074" data-mp="1pahu1t9mc04g">
            {HERO_SLIDE.headlineStart}
          </h1>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-cream italic leading-[1.05] mb-4" data-id="element-3075" data-mp="5g6vyrpxeyi1">
            {HERO_SLIDE.headlineEnd}
          </h1>
          <p className="text-sm md:text-base text-white/80 mb-5 max-w-md leading-relaxed" data-id="element-3076" data-mp="1dab0ucwyqax">
            {HERO_SLIDE.subhead}
          </p>
          <a href={HERO_SLIDE.primaryCta.href} className="inline-flex px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#4A5A5F] transition-colors" data-id="element-3077" data-mp="3nls4bt0z2ozv">
            {HERO_SLIDE.primaryCta.label}
          </a>

          <div className="grid grid-cols-3 gap-4 max-w-lg mt-6" data-id="element-3078" data-mp="k7fjmva9q9sj">
            {[{
            icon: Leaf,
            label: 'Dietitian Formulated'
          }, {
            icon: FlaskConical,
            label: 'Premium Ingredients'
          }, {
            icon: Sprout,
            label: 'No Artificial Fillers'
          }].map(badge => <div key={badge.label} className="flex items-center gap-2 text-left" data-id="element-3079" data-mp="17wgu0o2nvv0g">
                <badge.icon className="h-4 w-4 shrink-0 text-cream" data-id="element-3080" data-mp="131ulcsnysgk2" />
                <span className="text-[10px] text-white/85 leading-tight" data-id="element-3081" data-mp="1agdja4bol0qm">
                  {badge.label}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}
function ReviewsCarousel() {
  const reviewStats = useReviewStats();
  const totalReviews = Object.values(reviewStats).reduce((sum, stat) => sum + stat.count, 0);
  const weightedSum = Object.values(reviewStats).reduce((sum, stat) => sum + stat.avg * stat.count, 0);
  const overallAverage = totalReviews > 0 ? weightedSum / totalReviews : 0;
  const filledStars = Math.round(overallAverage);
  if (totalReviews === 0) {
    return <section className="bg-[#F9F9F9] py-16 md:py-24 px-6 border-t border-[#E5E5E5]" data-id="element-3082" data-mp="2833wg4bqw54c">
        <div className="max-w-3xl mx-auto text-center" data-id="element-3083" data-mp="2d2gsnfoiaojl">
          <div className="flex items-center justify-center gap-1 mb-6" data-id="element-3084" data-mp="2czubqpfq6wop">
            {[...Array(5)].map((_, index) => <Star key={index} className="w-6 h-6 text-[#D8D8D0]" data-id="element-3085" data-mp="hysab32rbxdh" data-mpi={index} />)}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F1F1F] leading-tight tracking-tight mb-4" data-id="element-3086" data-mp="czo1wennq3o7">
            No reviews yet
          </h2>
          <p className="text-base text-[#5A5A5A] leading-relaxed" data-id="element-3087" data-mp="qrvstps9f0ez">
            We just launched. Be the first to try our supplements and share your
            experience — your review could be the first one here.
          </p>
        </div>
      </section>;
  }
  return <section className="bg-[#F9F9F9] py-16 md:py-24 px-6 border-t border-[#E5E5E5]" data-id="element-3088" data-mp="vqyowkq9hwat">
      <div className="max-w-3xl mx-auto text-center" data-id="element-3089" data-mp="19pooq84gua2v">
        <div className="flex items-center justify-center gap-1 mb-6" data-id="element-3090" data-mp="19p5dfa9bmjxt">
          {[...Array(5)].map((_, index) => <Star key={index} className={`w-6 h-6 ${index < filledStars ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-3091" data-mp="15mijn3t3aoxz" data-mpi={index} />)}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#1F1F1F] leading-tight tracking-tight mb-4" data-id="element-3092" data-mp="2xd2sh6x80nrr">
          {totalReviews.toLocaleString()} Verified{' '}
          {totalReviews === 1 ? 'Review' : 'Reviews'} From Our Customers
        </h2>
        <p className="text-base text-[#5A5A5A] leading-relaxed" data-id="element-3093" data-mp="2sl2uhk0po4xt">
          {overallAverage.toFixed(1)} average rating. Every review comes from a
          verified buyer — click any product to read what customers are saying.
        </p>
      </div>
    </section>;
}